/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - CSS-модули</h2>
    <p className={styles.description}>
      Согласно определению из репозитория, CSS-модули — это CSS-файлы, в которых все классы по умолчанию находятся в
      локальной области видимости. Этот подход был разработан, что бы решить проблему глобальной области видимости в
      CSS.{' '}
    </p>
    <p className={styles.description}>
      CSS-модули — это не официальная спецификация, они не имплементированы в браузеры. Это процесс, запускаемый на
      стадии сборки проекта (например, с помощью Webpack), в результате выполнения которого имена классов и селекторы
      изменяются так, чтобы образовалась своего рода локальная область видимости (что-то вроде пространства имен).{' '}
    </p>
    <p className={styles.description}>CSS-модули гарантируют, что все стили одного компонента: </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>Находятся в одном месте</li>
      <li className={styles.list__item}>Применяются только к этому компоненту и никакому другому</li>
    </ul>
    <p className={styles.description}>
      Create React App поддерживает CSS-модули из коробки, все что необходимо сделать это называть файлы стилей согласно
      конвенции <code className={styles.code}>[name].module.css</code>.{' '}
    </p>
    <p className={styles.description}>
      Использование напоминает обычный CSS, за тем исключением, что все имена классов обфусцируются. В результирующем
      CSS получаем уникальное имя класса в формате{' '}
      <code className={styles.code}>[filename]\_[classname]\_\_[hash]</code>. CSS-модули решают проблему глобального
      пространсва имен с помощью мапинга классов из CSS-файла в объект с ключами по имени класса.{' '}
    </p>
    <CopyBlock
      text={`/* Button.module.css */
.button {
  display: inline-flex;
  margin: 0 4px;
  padding: 8px 24px;
  border: 0;
  border-radius: 2px;
  color: #ffffff;
  font-size: 14px;
  font-family: inherit;
  background-color: #2196f3;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
}

.button:hover,
.button:focus {
  background-color: #1976d2;
}

.disabled {
  background-color: #0000001f;
  color: #00000042;
}`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description} />
    <CopyBlock
      text={`// Button.js
import styles from './Button.module.css';

const Button = ({ type = 'button', label, disabled }) => {
  const btnClasses = [styles.button];

  if (disabled) {
    btnClasses.push(styles.disabled);
  }

  return (
    <button className={btnClasses.join(' ')} type={type} disabled={disabled}>
      {label}
    </button>
  );
};`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Композиция</h3>
      <p className={styles.description}>
        В CSS-модулях можно делать композицию стилей прямо в CSS-файле с помощью свойства{' '}
        <code className={styles.code}>composes</code>, как <code className={styles.code}>@extends</code> в{' '}
        <code className={styles.code}>SASS</code>.{' '}
      </p>
      <CopyBlock
        text={`/* Button.module.css */
.button {
  display: inline-flex;
  margin: 0 4px;
  padding: 8px 24px;
  border: 0;
  border-radius: 2px;
  font-size: 14px;
  font-family: inherit;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
}

.button:hover,
.button:focus {
  background-color: #1976d2;
}

.active {
  composes: button;
  color: #ffffff;
  background-color: #2196f3;
}

.disabled {
  composes: button;
  color: #00000042;
  background-color: #0000001f;
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description} />
      <CopyBlock
        text={`// Button.js
import styles from './Button.module.css';

const Button = ({ type = 'button', label, disabled }) => {
  const btnClass = disabled ? styles.disabled : styles.active;

  return (
    <button className={btnClass} type={type} disabled={disabled}>
      {label}
    </button>
  );
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Переменные</h3>
      <p className={styles.description}>В CSS-модулях также есть возможность использовать переменные. </p>
      <CopyBlock
        text={`/* variables.module.css */
@value font-stack: Roboto, Arial, sans-serif;

/* Button.module.css */
@value font-stack from 'path/to/variables.module.css';

.button {
  font-family: font-stack;
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Глобальные стили</h3>
      <p className={styles.description}>
        Для того чтобы сделать стиль глобальным, необходимо обернуть селектор в конструкцию{' '}
        <code className={styles.code}>:global(selector)</code>. Селекторы тегов по умолчанию будут в глобальной области
        видимости, CSS-модули обфусцируют только классы.{' '}
      </p>
      <CopyBlock
        text={`/* Глобален по умолчанию */
body {
  color: #fff;
  font-size: 16px;
}

/* Так класс можно сделать глобальным */
:global(.my-class) {
  color: #2196f3;
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
